import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { CalendarDto, CalendarEventDto } from '../../api/calendar';
import { calendarLabelOf } from './lib/calendarLabel';
import { typeMeta } from './calendarTypeMeta';
import { pendingResourceNames } from './ReservationBadge';
import { tCalendar } from '../../lib/calendar-i18n';
// 모양은 공용 Tooltip 의 것(.wc-tip)을 입는다 — 그 컴포넌트를 안 거치므로 규칙 파일을 직접 불러온다.
import '../../components/shared/Tooltip/Tooltip.css';
import './eventTooltip.css';

/**
 * 일정 칩 툴팁 (2026-09-09 사용자 결정) — 월간 칸이 좁아 제목이 잘리므로, 칩에 마우스를 올리면
 * 「제목 + 캘린더 + 작성자」를 스타일 툴팁으로 보여준다(⭐ 2026-10-27 부터 공용 Tooltip 의 모양·자리·400ms — 아래 TooltipState 위 주석)(브라우저 기본 title 툴팁 대체).
 *
 * 쓰는 법: CalendarPage 가 <EventTooltipProvider calendars={…}> 로 감싸고, 칩은
 * `{...useEventTooltip()(event)}` 로 핸들러(onMouseEnter/Leave, onFocus/Blur)만 얹는다.
 * Provider 가 없으면 핸들러가 빈 객체라 기존 렌더와 동일(additive).
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * ⚠ 정본(feature/dev-kyeongeun)에서 옮겨 온 판이다. 달라진 곳은 «다섯»이고 나머지 줄·주석·수치는
 *   정본 그대로다. ①② 는 PR #1856 · ③④ 는 PR #1868 · ⑤ 는 2026-10-01.
 *
 *   ① `calendarLabelOf` 를 여기서 «뺐다» — 이미 `lib/calendarLabel.ts` 에 한 벌 있다.
 *      정본 글자 그대로 옮기면 «같은 함수가 두 벌»이 되고, 한쪽만 고쳐지는 날이 온다.
 *      (정본 판은 `cal.orgTeamId`·`cal.orgTeamName` 갈래를 함께 봤는데, 그 두 칸은 지금
 *       `CalendarDto` 에 «없다» — 그 사정은 `lib/calendarLabel.ts` 머리말에 적혀 있다.)
 *   ② CSS 를 제 파일(`eventTooltip.css`)로 뺐다 — 정본은 `CalendarPage.css` 에 두었지만
 *      그 파일은 지금 여러 판이 동시에 건드리는 자리다. 값·선택자는 같고 «자리»만 다르다.
 *
 *   ③ 함움표에 «유형 이름»을 넣었다 — 정본에는 없다.
 *      까닭: 이 판은 칩의 `title` 을 지운다(안 지우면 브라우저 기본 툴팁과 «둘 다» 뜬다).
 *      그 중 `MonthView` 의 title 에만 유형 이름이 들어 있었으므로, 그것을 «같은 출처»에서
 *      되살려 «잃는 것을 0» 으로 만든다(2026-09-29 사용자 승인).
 *      ⛔ i18n 키를 새로 만들지 «않았다» — `typeMeta().label` 은 `TYPE_COLOR_MAP` 의 한국어라
 *        en·ja 에서도 한국어가 나온다. 그래도 새 키를 만들면 «같은 이름이 두 벌»이 된다.
 *        만료 조건(재는 법): `git grep -l TYPE_COLOR_MAP apps/frontend/src` 가 지금 «15개»다.
 *        그 label 이 i18n 화되는 판이 오면 이 툴팁은 «저절로» 따라온다 — 여기서 할 일이 없다.
 *   ④ `aria-describedby` 로 스크린리더를 이었다 — 정본에는 없다.
 *      까닭: 지우는 `title` 이 지금 스크린리더가 읽는 «유일한» 것이고, 이 폴더에
 *      `aria-describedby` 는 0건이었다. 지우면 그것이 사라진다(2026-09-29 사용자 승인).
 *      ⚠ 「툴팁은 한 번에 하나」이므로 id 는 하나로 족한데, 그 하나를 «모든 칩»에 늘 붙이면
 *        A 칩에 떠 있는데 B·C 칩도 「그 설명을 받는다」고 말한다 — 스크린리더에게 거짓이다.
 *        ⇒ 그래서 «뜬 그 칩에만» 붙인다(아래 `describedRef`).
 *      ⚠ id 는 `useId()` 로 만든다. 고정 문자열이어도 «지금은» 안 겹친다
 *        (CalendarPage 를 그리는 곳은 `router/index.tsx` 한 곳 — 재서 확인했다).
 *        `useId` 를 쓰는 값은 «같은 줄 수로 그 가정을 하나 덜 세우는» 것이다.
 *
 * ⛔ 만료 조건 — «일간 표(DayAgendaView)에는 붙이지 않았다».
 *    까닭: 일간은 2026-09-09 사용자 지시로 «표»가 됐고, 표는 칸이 넓어 제목이 안 잘린다.
 *    그것을 `eventTooltip.attach.test.tsx` 가 「핸들러 0건 · title 0건」으로 잠근다.
 * ──────────────────────────────────────────────────────────────────────────────
 */

/*
 * ⭐ 2026-10-27 — 겉모양·자리를 공용 `Tooltip`(정본 F5)과 «같게» 바꿨다(사용자 승인 · 전후 화면 확인).
 *   · 모양  : 반전 말풍선 — 공용 `.wc-tip` 클래스를 그대로 입는다(Tooltip.css 를 함께 불러온다).
 *   · 자리  : 칩 «위» 가운데. 위에 자리가 없을 때만 아래로 뒤집는다(공용과 같은 규칙 · 같은 간격 8px).
 *   · 지연  : 400ms(공용 기본값). 종전 120ms.
 *   ⚠ 공용 `<Tooltip>` 으로 칩을 «감싸지» 않은 까닭: 칩을 그리는 곳이 넷(MonthView · TimeGridStream ·
 *     TimeGridView · DayAgendaView)이고 칩마다 끌기 핸들러가 붙어 있어, 감싸는 요소를 하나 더 끼우면
 *     그 배치·끌기 경로를 건드리게 된다. ⇒ `EventTooltipProvider` / `useEventTooltip()` 꼴은 «그대로» 두고
 *     (부르는 쪽 0줄 변경) 그리는 상자만 공용의 모양·자리 규칙을 따른다.
 *   ⇒ 공용에 없는 둘은 «남겼다»: «뜬 그 칩에만» 붙는 `aria-describedby`(④) · 여러 줄 내용(제목·유형·예약).
 *     공용 `<Tooltip>` 은 자식에 aria 를 잇지 않는다.
 */
interface TooltipState { event: CalendarEventDto; rect: DOMRect }
interface Ctx {
  calendars: CalendarDto[];
  show: (event: CalendarEventDto, el: HTMLElement) => void;
  hide: () => void;
}

const EventTooltipContext = createContext<Ctx | null>(null);

/** 공용 Tooltip 의 기본 열기 지연과 같다. */
const SHOW_DELAY_MS = 400;
/** 공용 Tooltip 과 같은 값 — 칩과 말풍선 사이 · 말풍선 최대 폭 · 화면 가장자리 여백. */
const GAP = 8;
const MAX_W = 260;
const EDGE = 8;
/** 공용 Tooltip 이 「그쪽에 자리가 있다」고 보는 최소 높이. */
const FIT_MIN = 44;

export function EventTooltipProvider({ calendars, children }: { calendars: CalendarDto[]; children: ReactNode }) {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const timerRef = useRef<number | null>(null);
  const tooltipId = useId();
  /**
   * ④ 지금 `aria-describedby` 를 얹어 둔 칩. «칩 하나»만 들고 있는 것이 핵심이다 —
   *   일정 id 로 골라 얹으면 여러 날에 걸친 일정이 주마다 쪼개져 «같은 id 의 칩이 둘»이라
   *   둘 다 얹힌다. 뜬 그 순간의 «요소»를 들고 있으면 그런 일이 없다.
   */
  const describedRef = useRef<HTMLElement | null>(null);

  const clearTimer = () => { if (timerRef.current != null) { window.clearTimeout(timerRef.current); timerRef.current = null; } };
  const undescribe = () => {
    if (describedRef.current) { describedRef.current.removeAttribute('aria-describedby'); describedRef.current = null; }
  };
  const hide = useCallback(() => { clearTimer(); undescribe(); setTip(null); }, []);
  const show = useCallback((event: CalendarEventDto, el: HTMLElement) => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      undescribe();
      el.setAttribute('aria-describedby', tooltipId);
      describedRef.current = el;
      setTip({ event, rect: el.getBoundingClientRect() });
    }, SHOW_DELAY_MS);
  }, [tooltipId]);

  // 스크롤·리사이즈 중엔 위치가 어긋나니 그냥 닫는다. Esc 도 닫는다(공용 Tooltip 과 같다 — 눌러야 할 것을 가릴 수 있다).
  useEffect(() => {
    if (!tip) return;
    const off = () => { undescribe(); setTip(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') off(); };
    window.addEventListener('scroll', off, true);
    window.addEventListener('resize', off);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', off, true);
      window.removeEventListener('resize', off);
      window.removeEventListener('keydown', onKey);
    };
  }, [tip]);
  useEffect(() => () => { clearTimer(); undescribe(); }, []);

  return (
    <EventTooltipContext.Provider value={{ calendars, show, hide }}>
      {children}
      {tip && createPortal(<TooltipBox id={tooltipId} tip={tip} calendars={calendars} />, document.body)}
    </EventTooltipContext.Provider>
  );
}

type Place = 'top' | 'bottom';

/**
 * 첫 자리 — 공용 Tooltip 의 규칙 그대로: 위가 좁으면(44px 이하) 아래로, 가로는 칩 가운데를 최대 폭 기준으로 화면 안에 묶는다.
 * ⇒ 그린 뒤에는 «실제» 크기로 한 번 더 맞춘다(아래 `TooltipBox` 의 layout 효과) — 여러 줄이라 44px 보다 클 수 있다.
 */
function initialPos(r: DOMRect): { left: number; top: number; place: Place } {
  const fitsTop = r.top > FIT_MIN;
  const fitsBottom = window.innerHeight - r.bottom > FIT_MIN;
  const place: Place = fitsTop || !fitsBottom ? 'top' : 'bottom';
  const left = Math.min(Math.max(EDGE + MAX_W / 2, r.left + r.width / 2), window.innerWidth - MAX_W / 2 - EDGE);
  return { left, top: place === 'top' ? r.top - GAP : r.bottom + GAP, place };
}

function TooltipBox({ id, tip, calendars }: { id: string; tip: TooltipState; calendars: CalendarDto[] }) {
  const boxRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState(() => initialPos(tip.rect));

  // 그린 뒤 실제 크기로 보정 — 위에 «실제 높이»만큼 자리가 없을 때만 아래로 뒤집고(아래에 자리가 있을 때),
  // 가로는 실제 폭으로 화면 안에 묶는다. 칠하기 전에 맞춰야 한 번 반짝이지 않는다(layout 효과).
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const r = tip.rect;
    const w = box.offsetWidth, h = box.offsetHeight;
    const vw = window.innerWidth, vh = window.innerHeight;
    const roomTop = r.top - GAP - h >= EDGE;
    const roomBottom = r.bottom + GAP + h <= vh - EDGE;
    const first = initialPos(r);
    let place = first.place;
    if (h > 0) {
      if (place === 'top' && !roomTop && roomBottom) place = 'bottom';
      else if (place === 'bottom' && !roomBottom && roomTop) place = 'top';
    }
    const half = w > 0 ? w / 2 : MAX_W / 2;
    const left = Math.min(Math.max(EDGE + half, r.left + r.width / 2), vw - half - EDGE);
    setPos({ left, top: place === 'top' ? r.top - GAP : r.bottom + GAP, place });
  }, [tip]);

  const { event } = tip;
  return (
    <span
      ref={boxRef}
      id={id}
      className={`wc-tip wc-tip--${pos.place} calendar-event-tooltip`}
      style={{ left: pos.left, top: pos.top, maxWidth: MAX_W }}
      role="tooltip"
    >
      <span className="calendar-event-tooltip-title">{event.title}</span>
      {/*
        2026-09-09 사용자 결정: "캘린더 (작성자)" 한 줄 — "○○ 님이 등록" 은 길다.
        ③ 그 앞에 «유형 이름»을 붙였다 — 지우는 `MonthView` 의 title 이 갖고 있던 것이다.
           출처는 `typeMeta().label` «하나»뿐이다(칩의 아이콘도 같은 곳에서 온다).
      */}
      <span className="calendar-event-tooltip-meta">
        {typeMeta(event.type).label} · {calendarLabelOf(event, calendars)}{event.creator ? ` (${event.creator.name})` : ''}
      </span>
      {/*
        ⑤ 승인을 기다리는 회의실 (2026-10-01 사용자 결정 · 갈래 ⓒ) — 정본에는 없다.
        까닭: 이 툴팁이 뜨는 두 곳(월간 칩·주간 시간 블록)에서는 승인 배지가 제 `title` 을
        «빼므로»(안 그러면 둘 다 뜬다), 방 이름을 말할 자리가 여기밖에 없다.
        ⇒ 글자는 배지와 «같은 곳»에서 온다(`pendingResourceNames`) — 두 벌을 만들지 않는다.
        ⚠ 대기 중인 방이 없으면 이 줄 자체가 없다.
      */}
      {pendingResourceNames(event) && (
        <span className="calendar-event-tooltip-meta">
          {tCalendar('calendar.room.badge_pending')} · {pendingResourceNames(event)}
        </span>
      )}
    </span>
  );
}

type Handlers = {
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: () => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: () => void;
};

/** 칩에 얹을 핸들러를 만드는 함수 — Provider 밖이면 빈 객체(기존 동작 그대로). */
export function useEventTooltip(): (event: CalendarEventDto) => Handlers {
  const ctx = useContext(EventTooltipContext);
  return useCallback((event: CalendarEventDto): Handlers => {
    if (!ctx) return {};
    return {
      onMouseEnter: (e) => ctx.show(event, e.currentTarget),
      onMouseLeave: ctx.hide,
      onFocus: (e) => ctx.show(event, e.currentTarget),
      onBlur: ctx.hide,
    };
  }, [ctx]);
}
